/**
 * Miscellaneous Fees tab: table with row actions.
 */
import React, { useMemo } from 'react';
import {
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Table,
    Tbody,
    Td,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import { TbInfoCircle, TbPlus } from 'react-icons/tb';
import type { InvoiceLineItemsSectionProps } from './InvoiceLineItemsSection';
import { isCreditCardMiscFeeLine } from '../../utils/invoicePaySettlement';
import { getSignedNexLineDisplayQty, getSignedNexLineDisplayTotal } from '../../utils/nexLineQuantity';
import { formatNexMatchedLinePoLabel } from '../../utils/nexMatchedLinePoLabel';
import {
    INVOICE_TAB_ACCENT,
    INVOICE_TAB_ACCENT_HOVER,
    InvoiceIssueFlagButton,
    InvoicePartGridTableContainer,
    InvoiceMatchNoteInfoButton,
    InvoiceMatchedNexRows,
    InvoiceMatchedNexExpandAllHeaderControl,
    InvoiceMatchedNexToggleButton,
    InvoiceSuggestionBulbButton,
    InvoiceStandardHeaderRow,
    buildListPriceHeaderContent,
    getInvoiceItemsTableSx,
    InvoicePartTableRowActionsCell,
    VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON,
    invoicePartGridLandingInputPropsWithCaretAtEnd,
} from './InvoiceTabShared';
import { parseSignedQtyCommit } from '../../utils/invoiceNumericInput';
import { LineItemGridLandingEditable } from './LineItemGridLandingEditable';
import { useInvoicePartGridInteractions } from './useInvoicePartGridInteractions';
import { useInvoicePartTableRowDrag } from './useInvoicePartTableRowDrag';

const TESLA_CREDIT_RESTOCKING_FEE_TOOLTIP =
    'Auto-added from a Tesla credit memo where the part line’s Net price differed from its Total. The difference is treated as a restocking fee and submits to NEX as manualLineInd 2.';

export function InvoiceMiscellaneousFeesTab(props: InvoiceLineItemsSectionProps) {
    const {
        invoice,
        editedFields,
        addRestockingFeeItem,
        addRestockingFeeItemAt,
        addRestockingFeeItemAbove,
        duplicateRestockingFeeItemAt,
        removeRestockingFeeItem,
        reorderRestockingFeeItems,
        updateRestockingFeeItem,
        getRestockingFeeItemStringValue,
        renderRestockingFeeItemNumericCell,
        fmtUsd,
        isReadOnly,
        expandedMatchedNexRows,
        setExpandedMatchedNexRows,
        setUnmatchConfirmModal,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        openReconciliationDetailModal,
        getIssuesForPart,
        getPartNumberChoiceIssue,
        getMissingInNexSuggestion,
        getIssueTooltip,
        flaggedPartIndices,
        setEditingNumericField,
        setEditingNumericValues,
        onLineItemImageHighlightChange,
        showTableGridlines = true,
        effectiveMatchedNexLines,
        nexLineQuantityBasis = 'qtyRec',
    } = props as InvoiceLineItemsSectionProps & { isReadOnly?: boolean };
    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice?.fuelItems ?? [];
    const items = useMemo(
        () => editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [],
        [editedFields.restockingFeeItems, invoice?.restockingFeeItems]
    );
    const payCharge = (editedFields.paySettlementType ?? invoice?.paySettlementType) === 'charge';
    const visibleIndices = items
        .map((item, idx) => (payCharge && isCreditCardMiscFeeLine(item) ? null : idx))
        .filter((idx): idx is number => idx !== null);
    const hasItems = visibleIndices.length > 0;

    const spreadsheet = useInvoicePartGridInteractions({
        rowCount: items.length,
        rows: items,
        getStringValue: getRestockingFeeItemStringValue,
        updateCell: updateRestockingFeeItem,
        isReadOnly: !!isReadOnly,
        setEditingNumericField: setEditingNumericField ?? (() => {}),
        setEditingNumericValues,
        numericKeyPrefix: 'restockingFeeItem',
    });

    const rowDrag = useInvoicePartTableRowDrag({ isReadOnly: !!isReadOnly, onReorder: reorderRestockingFeeItems });

    const gridEditableInputProps = invoicePartGridLandingInputPropsWithCaretAtEnd();

    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const vendorMismatchDisablesSuggestions = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const roNotFound = !!invoice?.reconciliationIssues?.roNumberNotFound;
    const matchedNexLines = useMemo(
        () => effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [],
        [effectiveMatchedNexLines, invoice]
    );
    const baseMatchNotes =
        props.effectiveReconciliationMatchNotes ??
        (invoice?.reconciliationIssues as {
            reconciliationMatchNotes?: Array<{
                partIndex: number;
                invoicePartNumber: string;
                nexPartNumber: string;
                reason: string;
            }>;
        } | undefined)?.reconciliationMatchNotes ??
        [];

    const miscMatchedExpandKeys = useMemo(() => {
        const L = lineItems.length;
        const F = freightItems.length;
        const Fu = fuelItems.length;
        const vis = items
            .map((item, idx) => (payCharge && isCreditCardMiscFeeLine(item) ? null : idx))
            .filter((idx): idx is number => idx !== null);
        const keys: string[] = [];
        for (const index of vis) {
            const partIndexGlobal = L + F + Fu + index;
            let matchedNex = matchedNexLines.find((m: { partIndex: number; nexLine: any }) => m.partIndex === partIndexGlobal)?.nexLine;
            if (unmatchedPartIndices.has(partIndexGlobal)) matchedNex = undefined;
            const ov = localMatchedNexOverrides[partIndexGlobal];
            if (ov !== undefined) matchedNex = ov;
            if (matchedNex) keys.push(`misc-nex-${L}-${F}-${Fu}-${index}`);
        }
        return keys;
    }, [items, payCharge, lineItems.length, freightItems.length, fuelItems.length, matchedNexLines, unmatchedPartIndices, localMatchedNexOverrides]);

    const allMiscMatchedNexExpanded =
        miscMatchedExpandKeys.length > 0 && miscMatchedExpandKeys.every((k) => expandedMatchedNexRows.has(k));

    if (items.length > 0 && !hasItems) {
        return (
            <Box textAlign="center" py={6} color="gray.600" fontSize="sm" px={4}>
                Credit card fee lines are hidden for charge invoices (not payable on open account).
            </Box>
        );
    }

    if (!hasItems) {
        return (
            <Box textAlign="center" py={6}>
                <Button
                    size="sm"
                    bg={INVOICE_TAB_ACCENT}
                    color="white"
                    leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={4} />}
                    onClick={addRestockingFeeItem}
                    _hover={{ bg: INVOICE_TAB_ACCENT_HOVER }}
                    isDisabled={!!isReadOnly}
                >
                    Add Miscellaneous Fee Item
                </Button>
            </Box>
        );
    }

    return (
        <InvoicePartGridTableContainer
            containerRef={spreadsheet.containerRef}
            isReadOnly={!!isReadOnly}
            onMouseDownCapture={spreadsheet.onContainerMouseDownCapture}
            onPaste={spreadsheet.onPaste}
        >
            <Table variant="simple" size="sm" sx={getInvoiceItemsTableSx(showTableGridlines)}>
                <Thead>
                    <InvoiceStandardHeaderRow
                        firstColumnContent={
                            <InvoiceMatchedNexExpandAllHeaderControl
                                isVisible={miscMatchedExpandKeys.length > 0}
                                allExpanded={allMiscMatchedNexExpanded}
                                onToggle={() => {
                                    setExpandedMatchedNexRows((prev) => {
                                        const next = new Set(prev);
                                        if (allMiscMatchedNexExpanded) miscMatchedExpandKeys.forEach((k) => next.delete(k));
                                        else miscMatchedExpandKeys.forEach((k) => next.add(k));
                                        return next;
                                    });
                                }}
                            />
                        }
                        listPriceHeaderContent={
                            buildListPriceHeaderContent({
                                invoiceType: invoice?.invoiceType,
                                subletListPriceMarkup: invoice?.subletListPriceMarkup,
                                subletListPriceDiscount: invoice?.subletListPriceDiscount,
                                lkqListPriceMarkup: invoice?.lkqListPriceMarkup,
                                lkqListPriceDiscount: invoice?.lkqListPriceDiscount,
                            }) ?? 'List Price'
                        }
                    />
                </Thead>
                <Tbody>
                    {visibleIndices.map((index) => {
                        const partIndexGlobal = lineItems.length + freightItems.length + fuelItems.length + index;
                        const legacyRestockingIssuePartIndex = -(index + 1);
                        const restockingFeeIssue = invoice?.reconciliationIssues?.flaggedParts?.find(
                            (issue) => issue.issueType === 'restocking_fee_on_return' && issue.partIndex === legacyRestockingIssuePartIndex
                        );
                        let matchedNex = matchedNexLines.find((m: { partIndex: number; nexLine: any }) => m.partIndex === partIndexGlobal)?.nexLine as
                            | {
                                  partDesc?: string;
                                  partNumber?: string;
                                  qtyOnOrder?: number;
                                  qty?: number;
                                  listPrice?: number;
                                  unitCost?: number;
                                  extendedCost?: number;
                                  poHeaderID?: number;
                                  lineNumber?: number;
                                  unqSeq?: number | null;
                                  _poVendorName?: string;
                              }
                            | undefined;
                        if (unmatchedPartIndices.has(partIndexGlobal)) matchedNex = undefined;
                        const ov = localMatchedNexOverrides[partIndexGlobal];
                        if (ov !== undefined) matchedNex = ov;
                        const hasMatchedNex = !!matchedNex;
                        const expandKey = `misc-nex-${lineItems.length}-${freightItems.length}-${fuelItems.length}-${index}`;
                        const isNexExpanded = expandedMatchedNexRows.has(expandKey);
                        const nexQty = getSignedNexLineDisplayQty(
                            matchedNex,
                            invoice?.invoiceType,
                            nexLineQuantityBasis,
                        );
                        const nexTotalRaw = matchedNex?.extendedCost ?? Math.abs(nexQty) * (matchedNex?.unitCost ?? 0);
                        const nexTotal = getSignedNexLineDisplayTotal(nexTotalRaw, invoice?.invoiceType);
                        const nexMatchedVendorName = matchedNex?._poVendorName?.trim() ?? '';
                        const { label: nexMatchedPoLineLabel, status: nexMatchedStatus } = formatNexMatchedLinePoLabel(
                            matchedNex,
                            invoice?.nexResponse ?? null,
                        );
                        const matchNote = baseMatchNotes.find((n) => n.partIndex === partIndexGlobal);
                        const hasMatchNote = !!matchNote;
                        const isUserUnmatched = unmatchedPartIndices.has(partIndexGlobal);
                        const issues = getIssuesForPart(partIndexGlobal);
                        const isTeslaCreditImpliedRestockingFee =
                            items[index]?.teslaCreditImpliedRestockingFee === true;
                        const isFlagged =
                            !suppressFlagAndSuggestionUi &&
                            (flaggedPartIndices.has(partIndexGlobal) ||
                                flaggedPartIndices.has(legacyRestockingIssuePartIndex) ||
                                isUserUnmatched ||
                                !!restockingFeeIssue);
                        const isManuallyAdded = (items[index]?.confidence ?? 0) === 0;
                        const showRowHighlight = isManuallyAdded || (!hasMatchedNex && isFlagged);
                        return (
                            <React.Fragment key={index}>
                                <Tr
                                    bg={showRowHighlight ? 'orange.50' : 'white'}
                                    borderLeft={showRowHighlight ? '3px solid' : undefined}
                                    borderLeftColor={showRowHighlight ? 'orange.400' : undefined}
                                    _hover={{ bg: showRowHighlight ? 'orange.100' : 'gray.50' }}
                                    role="group"
                                    {...rowDrag.getDraggableRowProps(index)}
                                    onMouseEnter={() => {
                                        const rowHighlight = items[index]?.imageSourceHighlight;
                                        if (rowHighlight) {
                                            onLineItemImageHighlightChange?.(rowHighlight);
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        onLineItemImageHighlightChange?.(null);
                                    }}
                                >
                                    <Td py={3} px={2} borderColor="gray.100" position="relative" w="116px" minW="116px">
                                        <InvoicePartTableRowActionsCell
                                            isReadOnly={!!isReadOnly}
                                            onAddAbove={() => addRestockingFeeItemAbove(index)}
                                            onAddBelow={() => addRestockingFeeItemAt(index)}
                                            onDuplicate={() => duplicateRestockingFeeItemAt(index)}
                                            onDelete={() => removeRestockingFeeItem(index)}
                                        />
                                        <HStack spacing={0.5} align="center" justify="flex-start" pl={5} flexWrap="nowrap">
                                            {hasMatchedNex ? (
                                                <InvoiceMatchedNexToggleButton
                                                    isExpanded={isNexExpanded}
                                                    onClick={() =>
                                                        setExpandedMatchedNexRows((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(expandKey)) next.delete(expandKey);
                                                            else next.add(expandKey);
                                                            return next;
                                                        })
                                                    }
                                                />
                                            ) : null}
                                            {!suppressFlagAndSuggestionUi && !hasMatchedNex && isUserUnmatched ? (
                                                <InvoiceIssueFlagButton tooltip="Line item will be added to NEX without a matching NEX line" ariaLabel="Orphaned line item" />
                                            ) : null}
                                            {!suppressFlagAndSuggestionUi && !hasMatchedNex && !isUserUnmatched && (issues.length > 0 || !!restockingFeeIssue) ? (
                                                <InvoiceIssueFlagButton
                                                    tooltip={restockingFeeIssue ? 'Restocking fee on credit/return invoice' : getIssueTooltip(issues)}
                                                />
                                            ) : null}
                                            {!suppressFlagAndSuggestionUi && !hasMatchedNex && !roNotFound && !isReadOnly ? (
                                                <InvoiceSuggestionBulbButton
                                                    tooltip={
                                                        getPartNumberChoiceIssue(partIndexGlobal)
                                                            ? 'Part number differs from NEX'
                                                            : getMissingInNexSuggestion(partIndexGlobal)
                                                              ? 'Part not found – possible match'
                                                              : 'Click to view suggested matching miscellaneous fee line items from NEX'
                                                    }
                                                    onClick={() => {
                                                        const iss = getIssuesForPart(partIndexGlobal);
                                                        openReconciliationDetailModal({
                                                            type: 'flag',
                                                            partIndex: partIndexGlobal,
                                                            issue: iss[0],
                                                        });
                                                    }}
                                                />
                                            ) : null}
                                            {vendorMismatchDisablesSuggestions && !hasMatchedNex && !roNotFound && !isReadOnly ? (
                                                <InvoiceSuggestionBulbButton
                                                    tooltip={VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON}
                                                    disabled
                                                    ariaLabel="Suggestion disabled due to vendor mismatch"
                                                />
                                            ) : null}
                                            {hasMatchNote && matchNote ? (
                                                <InvoiceMatchNoteInfoButton
                                                    onClick={() =>
                                                        openReconciliationDetailModal({
                                                            type: 'match_note',
                                                            matchNote: {
                                                                invoicePartNumber: matchNote.invoicePartNumber,
                                                                nexPartNumber: matchNote.nexPartNumber,
                                                                reason: matchNote.reason,
                                                                partIndex: partIndexGlobal,
                                                            },
                                                        })
                                                    }
                                                />
                                            ) : null}
                                        </HStack>
                                    </Td>
                                    <Td
                                        py={3}
                                        fontSize="sm"
                                        textAlign="left"
                                        borderColor="gray.100"
                                        data-line-paste-cell
                                        data-row={index}
                                        data-col={0}
                                        sx={spreadsheet.getCellPasteSelectionSx(index, 0)}
                                    >
                                        <HStack spacing={1} align="center" w="100%">
                                            <Box flex="1" minW={0}>
                                                <LineItemGridLandingEditable
                                                    value={getRestockingFeeItemStringValue(index, 'title', '')}
                                                    onChange={(v) => updateRestockingFeeItem(index, 'title', v)}
                                                    placeholder="Description"
                                                    isReadOnly={!!isReadOnly}
                                                    editableRootProps={{ w: '100%' }}
                                                    editablePreviewProps={{
                                                        display: 'block',
                                                        w: '100%',
                                                        textAlign: 'left',
                                                        color: 'gray.900',
                                                        py: 0,
                                                        px: 0,
                                                    }}
                                                    editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                                />
                                            </Box>
                                            {isTeslaCreditImpliedRestockingFee ? (
                                                <Tooltip
                                                    label={TESLA_CREDIT_RESTOCKING_FEE_TOOLTIP}
                                                    placement="top"
                                                    hasArrow
                                                    openDelay={200}
                                                    maxW="320px"
                                                >
                                                    <IconButton
                                                        aria-label="Tesla credit restocking fee info"
                                                        icon={
                                                            <Icon
                                                                as={TbInfoCircle as React.ElementType}
                                                                boxSize={3.5}
                                                            />
                                                        }
                                                        variant="ghost"
                                                        size="xs"
                                                        minW="auto"
                                                        h="auto"
                                                        p={0}
                                                        color="gray.400"
                                                        _hover={{ color: 'gray.600', bg: 'transparent' }}
                                                        flexShrink={0}
                                                        tabIndex={-1}
                                                    />
                                                </Tooltip>
                                            ) : null}
                                        </HStack>
                                    </Td>
                                    <Td
                                        py={3}
                                        fontSize="sm"
                                        borderColor="gray.100"
                                        whiteSpace="nowrap"
                                        minW="120px"
                                        data-line-paste-cell
                                        data-row={index}
                                        data-col={1}
                                        sx={spreadsheet.getCellPasteSelectionSx(index, 1)}
                                    >
                                        <LineItemGridLandingEditable
                                            value={getRestockingFeeItemStringValue(index, 'partNumber', '')}
                                            onChange={(v) => updateRestockingFeeItem(index, 'partNumber', v)}
                                            placeholder="Part #"
                                            isReadOnly={!!isReadOnly}
                                            editableRootProps={{ flex: '1', minW: 0 }}
                                            editablePreviewProps={{
                                                w: '100%',
                                                cursor: 'pointer',
                                                color: 'gray.900',
                                                py: 0,
                                                px: 0,
                                                whiteSpace: 'nowrap',
                                            }}
                                            editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                        />
                                    </Td>
                                    <Td
                                        py={3}
                                        fontSize="sm"
                                        textAlign="right"
                                        borderColor="gray.100"
                                        data-line-paste-cell
                                        data-row={index}
                                        data-col={2}
                                        sx={spreadsheet.getCellPasteSelectionSx(index, 2)}
                                    >
                                        <LineItemGridLandingEditable
                                            value={getRestockingFeeItemStringValue(index, 'qty', '0')}
                                            onChange={(v) => updateRestockingFeeItem(index, 'qty', parseSignedQtyCommit(v))}
                                            isReadOnly={!!isReadOnly}
                                            editableRootProps={{ flex: '1', textAlign: 'right' }}
                                            editablePreviewProps={{
                                                w: '100%',
                                                cursor: 'pointer',
                                                textAlign: 'right',
                                                color: 'gray.900',
                                                py: 0,
                                                px: 0,
                                            }}
                                            editableInputProps={{
                                                type: 'text',
                                                inputMode: 'text',
                                                ...gridEditableInputProps,
                                                textAlign: 'right',
                                            }}
                                        />
                                    </Td>
                                    <Td
                                        py={3}
                                        fontSize="sm"
                                        textAlign="right"
                                        borderColor="gray.100"
                                        whiteSpace="nowrap"
                                        minW="90px"
                                        data-line-paste-cell
                                        data-row={index}
                                        data-col={3}
                                        sx={spreadsheet.getCellPasteSelectionSx(index, 3)}
                                    >
                                        {renderRestockingFeeItemNumericCell(index, 'listPrice')}
                                    </Td>
                                    <Td
                                        py={3}
                                        fontSize="sm"
                                        textAlign="right"
                                        borderColor="gray.100"
                                        whiteSpace="nowrap"
                                        minW="90px"
                                        data-line-paste-cell
                                        data-row={index}
                                        data-col={4}
                                        sx={spreadsheet.getCellPasteSelectionSx(index, 4)}
                                    >
                                        {renderRestockingFeeItemNumericCell(index, 'unitBuyPrice')}
                                    </Td>
                                    <Td
                                        py={3}
                                        fontSize="sm"
                                        textAlign="right"
                                        fontWeight="medium"
                                        borderColor="gray.100"
                                        whiteSpace="nowrap"
                                        minW="90px"
                                        data-line-paste-cell
                                        data-row={index}
                                        data-col={5}
                                        sx={spreadsheet.getCellPasteSelectionSx(index, 5)}
                                    >
                                        {renderRestockingFeeItemNumericCell(index, 'totalBuyPrice', 'medium')}
                                    </Td>
                                </Tr>
                                {hasMatchedNex ? (
                                    <InvoiceMatchedNexRows
                                        isExpanded={isNexExpanded}
                                        matchedNex={matchedNex}
                                        nexQty={nexQty}
                                        nexTotal={nexTotal}
                                        nexMatchedVendorName={nexMatchedVendorName}
                                        nexMatchedPoLineLabel={nexMatchedPoLineLabel ?? undefined}
                                        nexMatchedStatus={nexMatchedStatus ?? undefined}
                                        showTableGridlines={showTableGridlines}
                                        isReadOnly={!!isReadOnly}
                                        onUnmatch={() =>
                                            setUnmatchConfirmModal({
                                                partIndex: partIndexGlobal,
                                                partNumber: items[index]?.partNumber,
                                                nexPartNumber: matchedNex?.partNumber,
                                                nexLine: matchedNex,
                                            })
                                        }
                                        formatMoney={fmtUsd}
                                    />
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </Table>
        </InvoicePartGridTableContainer>
    );
}
